import React, { useEffect, useState, Fragment } from 'react';
import { withTranslation } from 'react-i18next';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import { Field, reduxForm } from 'redux-form';
import { ButtonToolbar, Button, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import Select from '../../../shared/components/form/Select';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import { relationEmployeeClassListByTermsQuery } from '../../../shared/queries/RelationEmployeeClass';
import GraphQL from '../../../GraphQL';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';

const EmployeeFilter = (props) => {
  const {
    t,
    filterClick,
    initialize,
    lastFilter,
    communicationChannels,
    accessProfile,
    subjects,
    schoolTerm,
    change,
  } = props;
  const [filter, setFilter] = useState();
  const [classes, setClasses] = useState([]);

  const filterChange = (field, value) => {
    const updatedFilter = { ...filter };
    updatedFilter[field] = value;
    setFilter(updatedFilter);
  };

  const filterCheckChange = (evt) => {
    if (evt) {
      const updatedFilter = { ...filter };
      updatedFilter[evt.target.name] = evt.target.checked;
      updatedFilter.filterByStatus = evt.target.checked;
      setFilter(updatedFilter);
    }
  };

  const loadSchoolClassBySchoolTerm = (selectedSchoolTerm) => {
    if (selectedSchoolTerm && selectedSchoolTerm.length) {
      const selectedSchoolTermSanitize = selectedSchoolTerm.map(
        ({ value, label, ...rest }) => rest
      );
      const dataQuery = relationEmployeeClassListByTermsQuery(
        0,
        selectedSchoolTermSanitize
      );
      GraphQL(dataQuery, {})
        .then(({ relationEmployeeClassListByTerms }) => {
          if (relationEmployeeClassListByTerms) {
            const schoolClassList = relationEmployeeClassListByTerms
              .reduce((acc, item) => acc.concat(item.class), [])
              .map((item) => ({
                ...item,
                value: item.id,
                label: item.name,
              }));
            setClasses(schoolClassList);
          }
        })
        .catch(() => {
          showNotificationAutoDestroy(
            'danger',
            t('errors.title'),
            t('errors.error-to-loading')
          );
        })
        .finally(() => {});
    } else {
      change('classes', []);
      setClasses([]);
      filterChange('classes', []);
      filterChange('terms', []);
    }
    change('classes', []);
  };

  useEffect(() => {
    setFilter(lastFilter);
    initialize(lastFilter);
  }, [lastFilter]);

  return (
    <MuiThemeProvider>
      <Fragment>
        <div className="form">
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.profiles')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="accessProfiles"
                component={Select}
                isMulti
                closeMenuOnSelect={false}
                options={accessProfile}
                placeholder={t('general.placeholders.select')}
                onChange={(value) => filterChange('accessProfiles', value)}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.communication-channels')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="channels"
                component={Select}
                isMulti
                closeMenuOnSelect={false}
                options={communicationChannels}
                placeholder={t('general.placeholders.select')}
                onChange={(value) => filterChange('channels', value)}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.school-terms')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="terms"
                component={Select}
                isMulti
                closeMenuOnSelect={false}
                options={schoolTerm}
                placeholder={t('general.placeholders.select')}
                onChange={(values) => {
                  filterChange('terms', values);
                  loadSchoolClassBySchoolTerm(values);
                }}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.school-classes')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="classes"
                component={Select}
                isMulti
                closeMenuOnSelect={false}
                options={classes}
                placeholder={t('general.placeholders.select')}
                onChange={(value) => filterChange('classes', value)}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.subjects')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="subjects"
                component={Select}
                isMulti
                closeMenuOnSelect={false}
                options={subjects}
                placeholder={t('general.placeholders.select')}
                onChange={(value) => filterChange('subjects', value)}
              />
            </div>
          </div>
          <div className="form__form-group-field">
            <Field
              name="active"
              component={renderCheckBoxField}
              defaultValue
              className="colored-click"
              onChange={filterCheckChange}
            />
            <Label className="generic__label">
              {t('general.only-actives')}
            </Label>
          </div>
        </div>
        <ButtonToolbar
          className="form__button-toolbar"
          style={{ justifyContent: 'flex-end' }}
        >
          <Button color="success" onClick={() => filterClick(filter)}>
            {t('general.filter')}
          </Button>
        </ButtonToolbar>
      </Fragment>
    </MuiThemeProvider>
  );
};

EmployeeFilter.propTypes = {
  t: PropTypes.func.isRequired,
  filterClick: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  lastFilter: PropTypes.shape(
    PropTypes.shape({
      studentsQuantity: PropTypes.number,
      updaterId: PropTypes.number,
      year: PropTypes.number,
      id: PropTypes.number,
      capacity: PropTypes.number,
      courseId: PropTypes.number,
      filterByStatus: PropTypes.bool,
      active: PropTypes.bool,
    })
  ).isRequired,
  communicationChannels: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    })
  ).isRequired,
  accessProfile: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    })
  ).isRequired,
  subjects: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    })
  ).isRequired,
  schoolTerm: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    })
  ).isRequired,
};

export default reduxForm({
  form: 'EmployeeFilterForm',
})(withTranslation('common')(EmployeeFilter));
